import { webApi } from '@mi-front/api-clients';
import Box from '@mui/material/Box';
import { forwardRef, useCallback } from 'react';
import { RangeBrush } from '../range-brush/RangeBrush';
import { useReportFullDomain, useStationFilterModel } from '../store/useViewModel';
import { TimelineAxisStrip } from '../timeline/axis/TimelineAxisStrip';
import { StationStatusFilter } from './StationStatusFilter';

export type TimelineFilterHeaderProps = {
  width: number;
  report: webApi.StreamQualityReportResponseDto;
};

/**
 * Sticky header above the timeline: station status chips, full report axis, and range brush.
 * Selected-range axes are rendered per timezone group with that group's IANA zone.
 * Ref attaches to the sticky root (for layout measurement); avoid wrapping this in an extra
 * shrink-wrapped box or `position: sticky` will not stick inside scrollable `main`.
 */
export const TimelineFilterHeader = forwardRef<HTMLDivElement, TimelineFilterHeaderProps>(
  function TimelineFilterHeader({ width, report }, forwardedRef) {
    const setRootRef = useCallback(
      (node: HTMLDivElement | null) => {
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    const fullDomain = useReportFullDomain(report);
    const { filteredStationIds } = useStationFilterModel(report);

    return (
      <Box
        ref={setRootRef}
        sx={(theme) => ({
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar - 1,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
          pt: 1,
          mt: -1,
          pb: 1,
        })}
      >
        <StationStatusFilter />
        <TimelineAxisStrip width={width} timeRange={fullDomain} />
        <RangeBrush width={width} report={report} minimapStationIds={filteredStationIds} />
      </Box>
    );
  },
);

TimelineFilterHeader.displayName = 'TimelineFilterHeader';
